import { inject, Injectable } from '@angular/core';
import { IApiErrorResponse } from '@core/models/api/api.models';
import { IProfilo } from '@core/models/user.model';
import { ApiService } from '@core/services/api/api.service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Console } from './logger.service';

/**
 * Interfaccia per la richiesta di creazione di un nuovo profilo amministratore.
 * Snake_case: il backend si aspetta e restituisce snake_case.
 */
export interface IProfileAdminCreateRequest {
  username: string;
  ruolo: string;
  password?: string;
  id_segnalatore?: number | null;
  id_anagrafica?: number | null;
  id_agenzia?: number | null;
  attributi?: string | null;
}

/**
 * Interfaccia per la richiesta di aggiornamento di un profilo amministratore.
 * Snake_case: il backend si aspetta e restituisce snake_case.
 * Tutti i campi sono opzionali per permettere aggiornamenti parziali.
 */
export interface IProfileAdminUpdateRequest {
  username?: string;
  ruolo?: string;
  password?: string;
  id_segnalatore?: number | null;
  id_anagrafica?: number | null;
  id_agenzia?: number | null;
  attributi?: string | null;
}

/**
 * Interfaccia per la richiesta di blocco/sblocco di un profilo amministratore.
 */
export interface IProfileAdminBlockRequest {
  bloccato: boolean;
}

/**
 * Servizio per la gestione amministrativa dei profili utente.
 *
 * Questo servizio fornisce metodi per tutte le operazioni CRUD sui profili
 * che richiedono privilegi amministrativi:
 * - Elenco profili di un utente
 * - Dettaglio singolo profilo
 * - Creazione nuovo profilo
 * - Aggiornamento dati profilo
 * - Blocco/sblocco profilo
 *
 * Tutte le chiamate utilizzano ApiService per la gestione HTTP e degli errori.
 *
 * **Standard Response API:**
 * - Risposte positive (HTTP 200): seguono lo standard `{ message: string, data?: T }`
 *   - Il campo `data` viene estratto automaticamente da ApiService
 * - Risposte di errore: seguono lo standard `IApiErrorResponse`
 *   - HTTP 422 (Unprocessable Content): errori di validazione con campo `errors` (oggetto con chiavi campo e valori array di messaggi)
 *   - HTTP 401 (Unauthorized): errore di autenticazione con campo `error`
 *   - HTTP 403 (Forbidden): errore di autorizzazione con campo `error`
 *   - Tutti gli errori hanno sempre il campo `status_code` e `message`
 *
 * Gli errori vengono normalizzati da ApiService e possono essere gestiti nei componenti
 * verificando `status_code` e i campi `errors` (per 422) o `error` (per 401/403).
 *
 * @example
 * ```typescript
 * constructor(private profilesAdminService: ProfilesAdminService) {}
 *
 * // Elenco profili di un utente
 * this.profilesAdminService.getUserProfiles(123).subscribe({
 *   next: (profiles) => console.log('Profili:', profiles),
 *   error: (error) => console.error('Errore:', error)
 * });
 *
 * // Creazione nuovo profilo
 * const newProfile: IProfileAdminCreateRequest = {
 *   username: 'MARIO.ROSSI',
 *   ruolo: 'SEGNALATORE',
 *   id_segnalatore: 1001,
 *   id_anagrafica: 500,
 *   id_agenzia: 1,
 *   attributi: ''
 * };
 * this.profilesAdminService.createProfile(123, newProfile).subscribe({
 *   next: (profile) => console.log('Profilo creato:', profile),
 *   error: (error) => console.error('Errore creazione:', error)
 * });
 * ```
 */

@Injectable({
  providedIn: 'root'
})
export class ProfilesAdminService {
  private readonly log_label = 'ProfilesAdminService';
  private readonly api = inject(ApiService);

  /**
   * Recupera l'elenco di tutti i profili associati a un utente specifico.
   *
   * @param userId - ID dell'utente di cui recuperare i profili
   * @returns Observable contenente l'array di profili dell'utente
   */
  getUserProfiles(userId: number): Observable<IProfilo[]> {
    const log_pref = this.log_label + '.getUserProfiles() ';
    return this.api.get<IProfilo[]>(`user/admin/users/${userId}/profiles`).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || `Errore nel recupero dei profili dell'utente con ID ${userId}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Recupera i dettagli di un singolo profilo appartenente all'utente indicato.
   *
   * @param userId - ID dell'utente proprietario del profilo
   * @param profileId - ID del profilo da recuperare
   * @returns Observable contenente i dati completi del profilo
   */
  getProfileById(userId: number, profileId: number): Observable<IProfilo> {
    const log_pref = this.log_label + '.getProfileById() ';
    return this.api.get<IProfilo>(`user/admin/users/${userId}/profiles/${profileId}`).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || `Errore nel recupero del profilo con ID ${profileId} dell'utente con ID ${userId}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea un nuovo profilo per un utente.
   *
   * @param userId - ID dell'utente a cui aggiungere il profilo
   * @param payload - Dati completi del nuovo profilo da creare
   * @returns Observable contenente i dati del profilo creato
   */
  createProfile(userId: number, payload: IProfileAdminCreateRequest): Observable<IProfilo> {
    const log_pref = this.log_label + '.createProfile() ';
    return this.api.post<IProfilo, IProfileAdminCreateRequest>(
      `user/admin/users/${userId}/profiles`,
      payload
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || `Errore nella creazione del nuovo profilo per l'utente con ID ${userId}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Aggiorna i dati di un profilo specifico appartenente all'utente.
   *
   * @param userId - ID dell'utente proprietario del profilo
   * @param profileId - ID del profilo da aggiornare
   * @param payload - Dati parziali da aggiornare (tutti i campi sono opzionali)
   * @returns Observable contenente i dati aggiornati del profilo
   */
  updateProfile(
    userId: number,
    profileId: number,
    payload: IProfileAdminUpdateRequest
  ): Observable<IProfilo> {
    const log_pref = this.log_label + '.updateProfile() ';
    return this.api.patch<IProfilo, IProfileAdminUpdateRequest>(
      `user/admin/users/${userId}/profiles/${profileId}`,
      payload
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || `Errore nell'aggiornamento del profilo con ID ${profileId} dell'utente con ID ${userId}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Blocca o sblocca un profilo.
   *
   * @param userId - ID dell'utente proprietario del profilo
   * @param profileId - ID del profilo da bloccare/sbloccare
   * @param blocked - true per bloccare il profilo, false per sbloccarlo
   * @returns Observable contenente i dati aggiornati del profilo o un messaggio di successo
   */
  setProfileBlocked(
    userId: number,
    profileId: number,
    blocked: boolean
  ): Observable<IProfilo | string> {
    const log_pref = this.log_label + '.setProfileBlocked() ';
    const payload: IProfileAdminBlockRequest = { bloccato: blocked };
    return this.api.patch<IProfilo | string, IProfileAdminBlockRequest>(
      `user/admin/users/${userId}/profiles/${profileId}/block`,
      payload
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const action = blocked ? 'blocco' : 'sblocco';
        const errorMessage = log_pref + (error?.message || `Errore nel ${action} del profilo con ID ${profileId} dell'utente con ID ${userId}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }
}
